import StatCounter from '../../common/StatCounter'
import { useRevealClass } from '../../../hooks/useRevealClass'
import { STATS } from './statsData'
import './StatsShowcaseSection.css'

function StatIcon({ icon }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {icon.circle && <circle cx={icon.circle.cx} cy={icon.circle.cy} r={icon.circle.r} />}
      {icon.polygon && <polygon points={icon.polygon} />}
      {icon.paths?.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {icon.polylines?.map((pts, i) => (
        <polyline key={i} points={pts.join(',')} />
      ))}
    </svg>
  )
}

function StatCard({ stat }) {
  const { ref, visible } = useRevealClass()
  return (
    <div
      ref={ref}
      className={`ss-card reveal${visible ? ' is-visible' : ''}`}
      style={{
        background: '#FFFFFF',
        borderRadius: '22px',
        padding: '36px 24px 32px',
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.05),0 1px 3px rgba(0,0,0,0.03)',
      }}
    >
      <div
        className="ss-icon-wrap"
        style={{
          width: '50px',
          height: '50px',
          borderRadius: '15px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '22px',
          background: 'linear-gradient(145deg,rgba(220,38,38,0.08),rgba(220,38,38,0.03))',
          border: '1px solid rgba(220,38,38,0.15)',
        }}
      >
        <StatIcon icon={stat.icon} />
      </div>

      <div className="ss-num-wrap" style={{ marginBottom: '10px', lineHeight: 1 }}>
        <StatCounter
          target={stat.target}
          suffix={stat.suffix}
          className="font-heading inline-block font-bold"
          style={{
            fontSize: 'clamp(2.4rem,4.5vw,3.25rem)',
            background: 'linear-gradient(135deg,#9B1C1C 0%,#DC2626 50%,#EF4444 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        />
      </div>

      <div
        className="ss-accent-line"
        style={{ width: '40px', height: '2px', borderRadius: '2px', marginBottom: '14px', background: 'linear-gradient(90deg,#DC2626,#F87171 70%,transparent)' }}
      />

      <p
        style={{
          fontSize: '11.5px',
          fontWeight: 600,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          color: 'rgba(0,0,0,0.48)',
          lineHeight: 1.45,
          fontFamily: "'Marcellus',serif",
        }}
      >
        {stat.label.split('\n').map((line, i, arr) => (
          <span key={line}>
            {line}
            {i < arr.length - 1 && <br />}
          </span>
        ))}
      </p>
    </div>
  )
}

export default function StatsShowcaseSection() {
  return (
    <section id="stats-showcase" className="relative overflow-hidden" style={{ background: '#FAFAF8', padding: '72px 0 84px' }}>
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{ background: 'radial-gradient(ellipse 90% 140% at 50% 40%,rgba(220,38,38,0.03) 0%,transparent 70%)' }}
      />
      <div
        className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2"
        aria-hidden="true"
        style={{ width: 'min(680px,80vw)', height: '1px', background: 'linear-gradient(90deg,transparent,rgba(220,38,38,0.22) 40%,rgba(220,38,38,0.22) 60%,transparent)' }}
      />
      <div
        className="pointer-events-none absolute top-5 left-6"
        aria-hidden="true"
        style={{ width: '24px', height: '24px', borderTop: '1.5px solid rgba(220,38,38,0.18)', borderLeft: '1.5px solid rgba(220,38,38,0.18)', borderRadius: '3px 0 0 0' }}
      />
      <div
        className="pointer-events-none absolute top-5 right-6"
        aria-hidden="true"
        style={{ width: '24px', height: '24px', borderTop: '1.5px solid rgba(220,38,38,0.18)', borderRight: '1.5px solid rgba(220,38,38,0.18)', borderRadius: '0 3px 0 0' }}
      />
      <div
        className="pointer-events-none absolute bottom-5 left-6"
        aria-hidden="true"
        style={{ width: '24px', height: '24px', borderBottom: '1.5px solid rgba(0,0,0,0.12)', borderLeft: '1.5px solid rgba(0,0,0,0.12)', borderRadius: '0 0 0 3px' }}
      />
      <div
        className="pointer-events-none absolute bottom-5 right-6"
        aria-hidden="true"
        style={{ width: '24px', height: '24px', borderBottom: '1.5px solid rgba(0,0,0,0.12)', borderRight: '1.5px solid rgba(0,0,0,0.12)', borderRadius: '0 0 3px 0' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              padding: '7px 20px',
              borderRadius: '100px',
              background: 'rgba(220,38,38,0.055)',
              border: '1px solid rgba(220,38,38,0.14)',
            }}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <circle cx="5" cy="5" r="3" fill="#DC2626" fillOpacity="0.85" />
              <circle cx="5" cy="5" r="4.5" stroke="#DC2626" strokeOpacity="0.30" strokeWidth="1" />
            </svg>
            <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(180,20,20,0.80)' }}>
              Numbers That Define Us
            </span>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <circle cx="5" cy="5" r="3" fill="#DC2626" fillOpacity="0.85" />
              <circle cx="5" cy="5" r="4.5" stroke="#DC2626" strokeOpacity="0.30" strokeWidth="1" />
            </svg>
          </span>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-7">
          {STATS.map((stat) => (
            <StatCard stat={stat} key={stat.target + stat.label} />
          ))}
        </div>
      </div>

      <div
        className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2"
        aria-hidden="true"
        style={{ width: 'min(680px,80vw)', height: '1px', background: 'linear-gradient(90deg,transparent,rgba(0,0,0,0.07) 40%,rgba(0,0,0,0.07) 60%,transparent)' }}
      />
    </section>
  )
}
